import axios from 'axios';
import intl from 'react-intl-universal';

import { API } from 'utils/api';
import { STATUS_FORBIDDEN, STATUS_NOT_FOUND } from 'utils/constants';

import { DistributionRepo, Distribution } from './Distribution';
import { buildDistribution, buildDistributionRequestDTO } from './factory';
import { LevelDTO } from 'student/api';

import { Notification, NotificationTypes } from 'components/common/Notification/Notification';

interface GrepDataDTO {
  displayName: string;
  code: string;
}

interface SchoolDTO {
  id: number;
  groupApiId : string;
  name: string;
  parent: string;
  address: string;
}

export interface DistributionStudentDTO {
  id: number;
  name: string;
  startDate: Date | null;
  endDate: Date | null;
  level: LevelDTO;
}

export interface DistributionGroupResponseDTO {
  id: number;
  groupName: string;
  school: SchoolDTO | null;
  grepData: GrepDataDTO | null;
  startDate: Date | null;
  endDate: Date | null;
  students: Array<DistributionStudentDTO>;
}

export interface DistributionResponseDTO {
  startDate: Date | null;
  endDate: Date | null;
  groups: Array<DistributionGroupResponseDTO>;
  referralLink: string;
  level: LevelDTO;
}

export interface DistributionStudentRequestDTO {
  studentId: number;
  startDate: string | null;
  endDate: string | null;
}

export interface DistributionGroupRequestDTO {
  groupId: number;
  startDate: string | null;
  endDate: string | null;
  assignedStudents: Array<DistributionStudentRequestDTO>;
}

export interface DistributionRequestDTO {
  assignedGroups: Array<DistributionGroupRequestDTO>;
}

/**
 * `catch (error: any)` let `error.response.status` be read without a check. That is not a
 * style point: on a network failure, a timeout or a CORS rejection axios throws an error
 * with **no `response` at all**, so the read threw a TypeError inside the catch block and
 * replaced the handled error with an unhandled one.
 *
 * These two narrow instead. A non-axios error — which is what a bug in this file would
 * throw — now reaches the default branch with its message rather than crashing the handler.
 */
const statusOf = (error: unknown): number | undefined =>
  (axios.isAxiosError(error) ? error.response?.status : undefined);

const messageOf = (error: unknown): string =>
  (error instanceof Error ? error.message : String(error));

export class DistributionApi implements DistributionRepo {

  public async getAssignmentDistributionData(id: number) {
    const groupsResponse = await API.get<DistributionResponseDTO>(`/api/teacher/assignments/${id}/distribute`);

    return buildDistribution(groupsResponse.data, id);
  }

  public async getTeachingPathDistributionData(id: number) {
    const groupsResponse = await API.get<DistributionResponseDTO>(`/api/teacher/teaching-paths/${id}/distribute`);
    return buildDistribution(groupsResponse.data, id);
  }

  public async saveAssignmentDistribution(distribution: Distribution) {
    try {
      await API.post(
        `api/teacher/assignments/${distribution.id}/distribute`,
        buildDistributionRequestDTO(distribution)
      );
    } catch (error) {
      Notification.create({
        type: NotificationTypes.ERROR,
        title: messageOf(error)
      });
    }
  }

  public async saveTeachingPathDistribution(distribution: Distribution) {
    try {
      await API.post(
        `/api/teacher/teaching-paths/${distribution.id}/distribute`,
        buildDistributionRequestDTO(distribution)
      );
    } catch (error) {
      Notification.create({
        type: NotificationTypes.ERROR,
        title: messageOf(error)
      });
    }
  }

  public async assignStudentToAssignment(assignmentId: string, referralToken: string): Promise<string> {
    try {
      await API.post(
        `/api/student/assignments/${assignmentId}/assign`,
        { referral_token: referralToken }
      );

      return '';
    } catch (error) {
      switch (statusOf(error)) {
        case STATUS_FORBIDDEN:
          return intl.get('distribution_page.assignment_cannot_be_assigned');
        case STATUS_NOT_FOUND:
          return intl.get('distribution_page.assignment_is_already_assigned');
        default:
          return messageOf(error);
      }
    }
  }

  public async assignStudentToTeachingPath(teachingPathId: string, referralToken: string): Promise<string> {
    try {
      await API.post(
        `/api/student/teaching-paths/${teachingPathId}/assign`,
        { referral_token: referralToken }
      );
      return '';
    } catch (error) {
      switch (statusOf(error)) {
        case STATUS_FORBIDDEN:
          return intl.get('distribution_page.teaching_path_cannot_be_assigned');
        case STATUS_NOT_FOUND:
          return intl.get('distribution_page.teaching_path_is_already_assigned');
        default:
          return messageOf(error);
      }
    }
  }
}
